import { useEffect, useRef, useState } from "react";
import type { StaffBookingResponse, StaffBookingSummaryResponse } from "@repo/api-client";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { useVenue } from "../shell/VenueContext";
import { Overlay } from "./ManualEntryDialog";
import { useDeclineRequest } from "./mutations";
import { bookingRejectionOf, rejectionMessageKey } from "./rejections";
import { banner, dangerButton, fieldLabel, secondaryButton, textInput } from "./styles";

/**
 * Decline a PENDING request-to-book booking (request-to-book). Releases the hold and notifies the guest
 * with an optional internal reason and an optional PROPOSED ALTERNATIVE time — a suggestion only, never
 * held or booked (the server live-checks it, AV-6; a stale slot comes back as a typed rejection here).
 * No manager step-up: declining a request is a day-to-day inbox action (R5).
 */
export function DeclineDialog({
  booking,
  onClose,
  onDeclined,
}: {
  booking: StaffBookingSummaryResponse;
  onClose: () => void;
  onDeclined: (b: StaffBookingResponse) => void;
}) {
  const { venueId } = useVenue();
  const { t } = useTranslation();
  const decline = useDeclineRequest(venueId, booking.bookingId ?? "");
  const headingRef = useRef<HTMLHeadingElement>(null);
  useEffect(() => headingRef.current?.focus(), []);

  const [reason, setReason] = useState("");
  const [alternative, setAlternative] = useState("");
  const [error, setError] = useState<string | null>(null);

  function submit() {
    setError(null);
    const proposedAlternativeStartSec = toEpochSec(alternative);
    decline.mutate(
      { version: booking.version ?? 0, reason: reason || null, proposedAlternativeStartSec },
      {
        onSuccess: (b) => onDeclined(b),
        onError: (err) => {
          const rej = bookingRejectionOf(err);
          setError(rej ? t(rejectionMessageKey(rej)) : t("bookings.error.declineFailed"));
        },
      },
    );
  }

  return (
    <Overlay onClose={onClose} label={t("bookings.decline.title")}>
      <h2
        ref={headingRef}
        tabIndex={-1}
        className={css({ fontSize: "lg", fontWeight: "bold", outline: "none" })}
      >
        {t("bookings.decline.title")}
      </h2>
      <p className={css({ fontSize: "sm", color: "fg.muted" })}>
        {t("bookings.decline.confirm", { ref: booking.externalBookingId ?? "" })}
      </p>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className={css({ display: "flex", flexDirection: "column", gap: "3" })}
      >
        {error ? (
          <p role="alert" className={banner("error")}>
            {error}
          </p>
        ) : null}

        <label className={fieldLabel}>
          {t("bookings.decline.reason")}
          <input className={textInput} value={reason} onChange={(e) => setReason(e.target.value)} />
        </label>

        <label className={fieldLabel}>
          {t("bookings.decline.proposedAlternative")}
          <input
            type="datetime-local"
            className={textInput}
            value={alternative}
            onChange={(e) => setAlternative(e.target.value)}
          />
          <span className={css({ fontSize: "xs", color: "fg.muted", fontWeight: "normal" })}>
            {t("bookings.decline.proposedHint")}
          </span>
        </label>

        <div
          className={css({
            display: "flex",
            gap: "2",
            justifyContent: "flex-end",
            flexWrap: "wrap",
          })}
        >
          <button type="button" className={secondaryButton} onClick={onClose}>
            {t("bookings.action.back")}
          </button>
          <button type="submit" className={dangerButton} disabled={decline.isPending}>
            {decline.isPending ? t("bookings.decline.declining") : t("bookings.decline.submit")}
          </button>
        </div>
      </form>
    </Overlay>
  );
}

/** A `datetime-local` value → epoch seconds (browser-local zone); the server live-checks the slot. */
function toEpochSec(value: string): number | undefined {
  if (!value) return undefined;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? undefined : Math.floor(ms / 1000);
}
